import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import {
    clone,
    cloneDeep,
    filter,
    findIndex,
    forEach,
    get,
    has,
    includes,
    isEmpty,
    isNull,
    map,
    merge,
    remove,
    some,
} from 'lodash';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';

import { OutsideClick } from 'common/outside-click/OutsideClick';
import { Overlay } from 'core/overlay/Overlay';
import { KeyCode } from 'core/util/KeyCode';
import 'common/dropdown-picker/DropdownPicker.scss';

export const DropDownPickerOverlayArea = 'dropdown-picker-overlay-area';

interface SearchOptions {
    enable?: boolean;
    defaultSearchText?: string;
    highlightSearchText?: boolean;
    /** will call onOutsideClick when there is no match in search */
    closeWhenNoMatch?: boolean;
}

interface DisplayOptions {
    width?: string;
    maxHeight?: string;
    /** to update height of the scrolling container when opening dropdown at Top, default: false */
    reviseMaxHeight?: boolean;
    displayDescription?: boolean;
    displayCategories?: boolean;
    displayFooter?: boolean;
    /** assign number of items to be displayed per row, default: 1 */
    countPerRow?: number;
}

export interface DropdownData {
    label: string;
    /** custom ui visualization, requires: label */
    customLabelElement?: JSX.Element;
    description?: string;
    /** custom ui visualization, requires: description  */
    customDescriptionElement?: JSX.Element;
    id?: string | number;
    additionalInfo?: any;
}

export interface DropdownDataWithHeader {
    header: string;
    customHeaderElement?: JSX.Element;
    content: DropdownData[];
    id?: string | number;
    /** icon name should be from internal icons list, will not be rendered if customIconElement is present */
    icon?: string;
    customIconElement?: JSX.Element;
    disabled?: boolean;
}

interface Handlers {
    onSelect: any;
    onOutsideClick?: any;
    onSearchTextChange?: any;
    onFetchMore?: any;
}

export interface Props {
    searchOptions?: SearchOptions;
    displayOptions?: DisplayOptions;
    dropdownData: (DropdownDataWithHeader | DropdownData)[];
    enableKeyNavigation?: boolean;
    enableAutoSelect?: boolean;
    autoFocus?: boolean;
    handlers: Handlers;
    hasDataLoaded?: boolean;
    hasOverlayPromise?: boolean;
    overlayArea?: string;
    enableInfiniteScroll?: boolean;
    itemsPerPage?: number;
    onFetchMore?: any;
}

const ContainerStyles = {
    Categories: {
        maxHeight: 36,
    },
    Search: {
        maxHeight: 56,
    },
    Footer: {
        maxHeight: 47,
    },
};

const SearchBar = ({
    inputRef,
    searchText,
    enableAutoSelect,
    handleSearchTextChange,
    handleKeyNavigation,
}: {
    inputRef: any;
    searchText: string;
    enableAutoSelect: boolean;
    handleSearchTextChange: any;
    handleKeyNavigation: any;
}): JSX.Element | null => {
    const { t } = useTranslation('common');

    return (
        <div
            className="dropdown-picker-search-bar"
            style={{
                width: enableAutoSelect ? '90%' : '100%',
            }}
        >
            <input
                ref={inputRef}
                type="text"
                aria-label={t('SEARCH')}
                value={searchText}
                onChange={handleSearchTextChange}
                onKeyDown={handleKeyNavigation}
            />
            <i className="icon-search" />
        </div>
    );
};

const AutoSelect = ({ handleAutoSelect }: { handleAutoSelect: any }): JSX.Element | null => {
    const { t } = useTranslation(['common']);

    return (
        <OverlayTrigger placement="top" overlay={<Tooltip id="add-objective-tooltip">{t('AUTO_SELECT')}</Tooltip>}>
            <button className="title-tooltip ml-2 dropdown-picker-auto-select" onClick={handleAutoSelect} type="button">
                <img src="/rsrc2/images/admin/common/random.svg" alt={t('AUTO_SELECT')} />
            </button>
        </OverlayTrigger>
    );
};

const SearchContainer = ({
    inputRef,
    searchOptions,
    searchText,
    enableAutoSelect,
    handleSearchTextChange,
    handleKeyNavigation,
    handleAutoSelect,
}: {
    inputRef: any;
    searchOptions: SearchOptions;
    searchText: string;
    enableAutoSelect: boolean;
    handleSearchTextChange: any;
    handleKeyNavigation: any;
    handleAutoSelect: any;
}): JSX.Element => {
    return (
        <article
            className="dropdown-picker-search-container"
            style={{
                maxHeight: `${ContainerStyles.Search.maxHeight}px`,
            }}
        >
            {searchOptions.enable! ? (
                <SearchBar
                    inputRef={inputRef}
                    searchText={searchText}
                    handleSearchTextChange={handleSearchTextChange}
                    handleKeyNavigation={handleKeyNavigation}
                    enableAutoSelect={enableAutoSelect}
                />
            ) : null}
            {enableAutoSelect ? <AutoSelect handleAutoSelect={handleAutoSelect} /> : null}
        </article>
    );
};

const splitText = (inputText: string, searchQuery: string): string[] => {
    return inputText.split(new RegExp(`(${searchQuery})`, 'gi'));
};

const HighlightText = ({
    className,
    inputText,
    searchText,
    highlightSearchText,
}: {
    className: string;
    inputText: string;
    searchText: string;
    highlightSearchText: boolean;
}): JSX.Element => {
    return (
        <div className={className}>
            {inputText && searchText && highlightSearchText ? (
                map(splitText(inputText, searchText), (partItem: string, partKey: number) => (
                    <span
                        key={partKey}
                        style={partItem.toLowerCase() === searchText!.toLowerCase() ? { fontWeight: 'bold' } : {}}
                    >
                        {partItem}
                    </span>
                ))
            ) : (
                <>{inputText}</>
            )}
        </div>
    );
};

const ItemContainer = ({
    index,
    parentIndex,
    label,
    customLabelElement,
    description,
    customDescriptionElement,
    searchText,
    highlightSearchText,
    headerIndex,
    contentIndex,
    displayOptions,
    handleItemMouseOver,
    handleItemSelect,
}: {
    index: number;
    parentIndex: number | null;
    label: string;
    customLabelElement?: JSX.Element;
    description: string;
    customDescriptionElement?: JSX.Element;
    searchText: string;
    highlightSearchText: boolean;
    headerIndex: number | null;
    contentIndex: number;
    displayOptions: DisplayOptions;
    handleItemMouseOver: any;
    handleItemSelect: any;
}): JSX.Element | null => {
    let labelRenderer = customLabelElement || null;
    let descriptionRenderer = customDescriptionElement || null;

    if (!labelRenderer && label) {
        labelRenderer = (
            <HighlightText
                className="data-item-label"
                inputText={label}
                searchText={searchText}
                highlightSearchText={highlightSearchText}
            />
        );
    }

    if (!descriptionRenderer && description && displayOptions!.displayDescription) {
        descriptionRenderer = (
            <HighlightText
                className="data-item-description"
                inputText={description!}
                searchText={searchText}
                highlightSearchText={highlightSearchText}
            />
        );
    }

    return (
        <div
            role="button"
            tabIndex={0}
            className={`data-container-item ${
                index === contentIndex && (isNull(parentIndex) || parentIndex === headerIndex) ? 'item-focus' : ''
            }`}
            style={{
                width: `${displayOptions!.countPerRow ? 100 / displayOptions!.countPerRow : 100}%`,
            }}
            onMouseOver={(): void => {
                handleItemMouseOver(parentIndex, index);
            }}
            onFocus={(): void => {
                handleItemMouseOver(parentIndex, index);
            }}
            onClick={(): void => {
                handleItemSelect();
            }}
            onKeyPress={(): void => {
                // Do Nothing
            }}
        >
            {labelRenderer}
            {descriptionRenderer}
        </div>
    );
};

const getRevisedMaxHeight = ({
    wrapperRef,
    displayOptions,
    searchOptions,
}: {
    wrapperRef: any;
    displayOptions: DisplayOptions;
    searchOptions: SearchOptions;
}): any => {
    const currMaxHeight = parseInt(displayOptions!.maxHeight!.replace('px', ''), 10);
    let newMaxHeight = currMaxHeight;

    if (displayOptions!.reviseMaxHeight && wrapperRef && wrapperRef.current) {
        const bufferHeight = 15 + 50; // 50 for top header
        const searchHeight = searchOptions!.enable ? ContainerStyles.Search.maxHeight : 0;
        const categoriesHeight = displayOptions!.displayCategories ? ContainerStyles.Categories.maxHeight : 0;
        const footerHeight = displayOptions!.displayFooter ? ContainerStyles.Footer.maxHeight : 0;
        const wrapperBounds = wrapperRef.current.getBoundingClientRect();
        const totalWidgetHeight = searchHeight + categoriesHeight + footerHeight + bufferHeight;

        if (wrapperBounds && wrapperBounds.bottom && wrapperBounds.bottom < currMaxHeight + totalWidgetHeight) {
            newMaxHeight = wrapperBounds.bottom - totalWidgetHeight;
        }
    }
    return `${newMaxHeight}px`;
};

const DropdownDataContainer = ({
    wrapperRef,
    searchOptions,
    searchText,
    filteredData,
    hasDataLoaded,
    displayOptions,
    headerIndex,
    contentIndex,
    containerRef,
    handleItemMouseOver,
    handleItemSelect,
    hasOverlayPromise,
    overlayArea,
    onLoadMore,
    hasMore,
    isLoadingMore,
    handlers,
}: {
    wrapperRef: any;
    searchOptions: SearchOptions;
    searchText: string;
    filteredData: (DropdownDataWithHeader | DropdownData)[];
    hasDataLoaded: boolean;
    displayOptions: DisplayOptions;
    headerIndex: number;
    contentIndex: number;
    containerRef: any;
    handleItemMouseOver: any;
    handleItemSelect: any;
    hasOverlayPromise: any;
    overlayArea: string;
    onLoadMore?: () => void;
    hasMore?: boolean;
    isLoadingMore?: boolean;
    handlers?: Handlers;
}): JSX.Element => {
    const { t } = useTranslation(['common']);

    if (hasDataLoaded && (!filteredData || !filteredData.length)) {
        return <div className="data-container-empty">{t('NO_DATA_TO_DISPLAY')}</div>;
    }
    const newMaxHeight = getRevisedMaxHeight({ wrapperRef, displayOptions, searchOptions });

    return (
        <article className="dropdown-picker-data-container">
            <div
                className="data-container-scroll"
                ref={containerRef}
                style={{ maxHeight: newMaxHeight, overflowY: 'auto' }}
            >
                <InfiniteScroll
                    pageStart={0}
                    initialLoad={false}
                    loadMore={(page: number) => {
                        if (onLoadMore) {
                            onLoadMore();
                        }
                    }}
                    hasMore={!!hasMore && !isLoadingMore}
                    useWindow={false}
                    getScrollParent={() => containerRef.current}
                    loader={
                        <div className="dropdown-loading" key="dropdown-loading">
                            {t('LOADING')}...
                        </div>
                    }
                >
                    {hasOverlayPromise ? <Overlay area={overlayArea} /> : null}
                    {map(filteredData, (dataItem: DropdownDataWithHeader | DropdownData, dataKey: number) => {
                        let headerElement = (dataItem as DropdownDataWithHeader).customHeaderElement || null;

                        if (!headerElement && (dataItem as DropdownDataWithHeader).header) {
                            headerElement = (
                                <div className="data-container-header">
                                    {(dataItem as DropdownDataWithHeader).header}
                                </div>
                            );
                        }
                        return (
                            <div key={dataKey}>
                                {headerElement}
                                {(dataItem as DropdownDataWithHeader).header ? (
                                    map(
                                        (dataItem as DropdownDataWithHeader).content,
                                        (contentItem: DropdownData, contentKey: number) => (
                                            <ItemContainer
                                                key={`${dataKey}-${contentKey}`}
                                                index={contentKey}
                                                parentIndex={dataKey}
                                                label={contentItem.label}
                                                customLabelElement={contentItem.customLabelElement}
                                                description={contentItem.description!}
                                                customDescriptionElement={contentItem.customDescriptionElement}
                                                searchText={searchText}
                                                highlightSearchText={searchOptions.highlightSearchText!}
                                                headerIndex={headerIndex}
                                                contentIndex={contentIndex}
                                                displayOptions={displayOptions!}
                                                handleItemMouseOver={handleItemMouseOver}
                                                handleItemSelect={handleItemSelect}
                                            />
                                        ),
                                    )
                                ) : (
                                    <ItemContainer
                                        key={dataKey}
                                        index={dataKey}
                                        parentIndex={null}
                                        label={(dataItem as DropdownData).label}
                                        customLabelElement={(dataItem as DropdownData).customLabelElement}
                                        description={(dataItem as DropdownData).description!}
                                        customDescriptionElement={(dataItem as DropdownData).customDescriptionElement}
                                        searchText={searchText}
                                        headerIndex={null}
                                        contentIndex={contentIndex}
                                        highlightSearchText={searchOptions.highlightSearchText!}
                                        displayOptions={displayOptions!}
                                        handleItemMouseOver={handleItemMouseOver}
                                        handleItemSelect={handleItemSelect}
                                    />
                                )}
                            </div>
                        );
                    })}
                </InfiniteScroll>
            </div>
        </article>
    );
};

const Categories = ({
    data,
    categoryIndex,
    handleCategorySelection,
}: {
    data: any;
    categoryIndex: number;
    handleCategorySelection: any;
}): JSX.Element | null => {
    if (data && data.length) {
        return (
            <div
                className="dropdown-category-container"
                style={{
                    height: `${ContainerStyles.Categories.maxHeight}px`,
                }}
            >
                {map(data, (dataItem, dataKey) => {
                    let iconElement = <div className="category-label">{dataItem.label}</div>;

                    if (dataItem.customIconElement) {
                        iconElement = dataItem.customIconElement;
                    } else if (dataItem.icon) {
                        iconElement = <i className={dataItem.icon} />;
                    }
                    return (
                        <div
                            key={dataKey}
                            role="button"
                            tabIndex={0}
                            className={`category-item ${parseInt(dataKey, 10) === categoryIndex ? 'selected' : ''} ${
                                dataItem.disabled ? 'disabled' : ''
                            }`}
                            style={{
                                width: `${100 / data.length}%`,
                            }}
                            onClick={(): void => {
                                handleCategorySelection(parseInt(dataKey, 10), dataItem.disabled);
                            }}
                            onKeyPress={(): void => {
                                // Do Nothing
                            }}
                        >
                            {iconElement}
                        </div>
                    );
                })}
            </div>
        );
    }
    return null;
};

const Footer = ({
    displayOptions,
    data,
    searchText,
}: {
    displayOptions: DisplayOptions;
    data: DropdownData | null;
    searchText: string;
}): JSX.Element | null => {
    if (!isNull(data)) {
        return (
            <div
                className="dropdown-footer-container"
                style={{
                    maxHeight: `${ContainerStyles.Footer.maxHeight}px`,
                }}
            >
                <span className="footer-label">{data?.label}</span>
                <HighlightText
                    className="footer-description"
                    inputText={data?.description!}
                    searchText={searchText}
                    highlightSearchText
                />
            </div>
        );
    }
    return null;
};

export const DropdownPicker = forwardRef<any, Props>(
    (
        {
            searchOptions,
            displayOptions,
            dropdownData,
            hasDataLoaded,
            hasOverlayPromise,
            enableAutoSelect,
            enableKeyNavigation,
            autoFocus,
            overlayArea,
            handlers,
            enableInfiniteScroll,
            itemsPerPage,
        },
        ref: any,
    ) => {
        const wrapperRef = useRef<HTMLDivElement>(null);
        const dataContainerRef = useRef<HTMLDivElement>(null);
        const inputRef = useRef<HTMLInputElement>(null);
        const [searchText, setSearchText] = useState('');
        const [filteredData, setFilteredData] = useState(dropdownData);
        const [headerIndex, setHeaderIndex] = useState(0);
        const [contentIndex, setContentIndex] = useState(0);
        const [categoryIndex, setCategoryIndex] = useState(0);
        const [allData, setAllData] = useState<(DropdownDataWithHeader | DropdownData)[]>(dropdownData);
        const [isLoadingMore, setIsLoadingMore] = useState(false);
        const [hasMoreData, setHasMoreData] = useState(true);
        const [currentOffset, setCurrentOffset] = useState(0);
        const defaultItemsPerPage = itemsPerPage || 50;
        const mergedSearchOptions = merge({}, clone(DropdownPicker.defaultProps?.searchOptions), searchOptions);
        const mergedDisplayOptions = merge({}, clone(DropdownPicker.defaultProps?.displayOptions), displayOptions);

        const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
        const defaultSearchText = mergedSearchOptions?.defaultSearchText;

        useEffect(() => {
            setSearchText(defaultSearchText || '');
        }, [defaultSearchText]);

        useEffect(() => {
            setAllData(dropdownData);
            setFilteredData(dropdownData);
            setCurrentOffset(dropdownData.length);
            setHasMoreData(dropdownData.length >= defaultItemsPerPage);
        }, [dropdownData, defaultItemsPerPage]);
        useEffect((): any => {
            if (inputRef && autoFocus) {
                const node = inputRef?.current;

                if (node !== null) {
                    node.focus();
                }
            }
            if (searchOptions!.defaultSearchText || (allData && allData.length)) {
                handleFilteredData(searchOptions!.defaultSearchText, allData);
            }
        }, [searchOptions, allData]); // eslint-disable-line react-hooks/exhaustive-deps
        useImperativeHandle(ref, () => ({
            handleKeyNavigation,
        }));
        const includeRecord = (label = '', description = '', searchQuery = ''): boolean => {
            return (
                (!isEmpty(label) || !isEmpty(description)) &&
                (!searchQuery ||
                    includes((label || '').toLowerCase(), searchQuery?.toLowerCase()) ||
                    includes((description || '').toLowerCase(), searchQuery?.toLowerCase()))
            );
        };

        const doSearchTextChange = async (newSearchText: string): Promise<void> => {
            setHeaderIndex(0);
            setContentIndex(0);
            setIsLoadingMore(false);

            if (handlers?.onSearchTextChange) {
                setIsLoadingMore(true);
                try {
                    await handlers.onSearchTextChange(newSearchText, 0, defaultItemsPerPage);
                } catch (error) {
                    console.error('Error fetching search data:', error);
                } finally {
                    setIsLoadingMore(false);
                }
            } else {
                handleFilteredData(newSearchText, allData);
            }
        };

        useEffect(() => {
            setAllData(dropdownData);
            setFilteredData(dropdownData);
            setCurrentOffset(dropdownData.length);
            setHasMoreData(dropdownData.length >= defaultItemsPerPage);
        }, [dropdownData, defaultItemsPerPage]);

        const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
            const newSearchText = event.target.value;

            setSearchText(newSearchText);

            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            debounceTimeoutRef.current = setTimeout(() => {
                doSearchTextChange(newSearchText);
            }, 300);
        };

        useEffect(() => {
            return () => {
                if (debounceTimeoutRef.current) {
                    clearTimeout(debounceTimeoutRef.current);
                }
            };
        }, []);

        const handleFilteredData = (searchString = '', dataToFilter = allData): void => {
            let data: (DropdownDataWithHeader | DropdownData)[] = [];

            data = filter(cloneDeep(dataToFilter), (dataItem, dataKey: number) => {
                if ((dataItem as DropdownDataWithHeader).header) {
                    remove((dataItem as DropdownDataWithHeader).content, (contentItem: any, contentKey: any) => {
                        return !includeRecord(contentItem.label, contentItem.description, searchString);
                    });
                    return (dataItem as DropdownDataWithHeader).content.length > 0;
                }
                return includeRecord(
                    (dataItem as DropdownData).label,
                    (dataItem as DropdownData)!.description,
                    searchString,
                );
            });
            if (
                allData &&
                allData.length &&
                (!data || !data.length) &&
                searchText &&
                mergedSearchOptions!.closeWhenNoMatch
            ) {
                handleOutsideClick();
            } else {
                setFilteredData(data);
            }
        };

        const handleLoadMore = async (): Promise<void> => {
            if (!handlers.onFetchMore || isLoadingMore || !hasMoreData) {
                return;
            }
            setIsLoadingMore(true);
            try {
                const newData = await handlers.onFetchMore(searchText, currentOffset, defaultItemsPerPage);

                if (newData && newData.length > 0) {
                    const updatedAllData = [...allData, ...newData];

                    setAllData(updatedAllData);
                    setFilteredData(updatedAllData);
                    setCurrentOffset(currentOffset + newData.length);
                    setHasMoreData(newData.length >= defaultItemsPerPage);
                } else {
                    setHasMoreData(false);
                }
            } catch (error) {
                console.error('Error loading more data:', error);
                setHasMoreData(false);
            } finally {
                setIsLoadingMore(false);
            }
        };

        const handleKeyNavigation = (event: React.KeyboardEvent): void => {
            if (event.keyCode === KeyCode.Esc || (enableKeyNavigation && filteredData && filteredData.length)) {
                const dataHasHeader: boolean = hasHeader();
                const countPerRow = mergedDisplayOptions!.countPerRow || 1;
                let headerIterator = headerIndex;
                let contentIterator = contentIndex;

                switch (event.keyCode) {
                    case KeyCode.ArrowDown:
                        if (dataHasHeader) {
                            if (
                                (filteredData[headerIterator] as DropdownDataWithHeader).content.length - countPerRow >
                                contentIterator
                            ) {
                                contentIterator += countPerRow;
                            } else {
                                const currentRow = Math.floor(contentIterator / countPerRow);
                                const totalRows = Math.floor(
                                    ((filteredData as DropdownDataWithHeader[])[headerIterator].content.length - 1) /
                                        countPerRow,
                                );

                                if (currentRow !== totalRows) {
                                    contentIterator =
                                        (filteredData as DropdownDataWithHeader[])[headerIterator].content.length - 1;
                                } else if (filteredData!.length - 1 > headerIterator) {
                                    headerIterator += 1;
                                    contentIterator = Math.min(
                                        (filteredData as DropdownDataWithHeader[])[headerIterator].content.length - 1,
                                        contentIterator % countPerRow,
                                    );
                                }
                            }
                        } else if (filteredData.length - countPerRow > contentIterator) {
                            contentIterator += countPerRow;
                        }
                        break;
                    case KeyCode.ArrowUp:
                        if (dataHasHeader) {
                            if (contentIterator - countPerRow >= 0) {
                                contentIterator -= countPerRow;
                            } else if (headerIterator > 0) {
                                headerIterator -= 1;
                                const mod =
                                    (filteredData[headerIterator] as DropdownDataWithHeader).content.length %
                                    countPerRow;

                                if (contentIterator >= mod) {
                                    contentIterator =
                                        (filteredData[headerIterator] as DropdownDataWithHeader).content.length - 1;
                                } else {
                                    const divisor = Math.floor(
                                        ((filteredData[headerIterator] as DropdownDataWithHeader).content.length - 1) /
                                            countPerRow,
                                    );

                                    contentIterator = divisor * countPerRow + contentIterator;
                                }
                            }
                        } else if (contentIterator > countPerRow - 1) {
                            contentIterator -= countPerRow;
                        }
                        break;
                    case KeyCode.ArrowRight:
                        if (dataHasHeader) {
                            if (
                                (filteredData[headerIterator] as DropdownDataWithHeader).content.length - 1 >
                                contentIterator
                            ) {
                                contentIterator += 1;
                            } else if (filteredData.length - 1 > headerIterator) {
                                headerIterator += 1;
                                contentIterator = 0;
                            }
                        } else if (filteredData.length - 1 > contentIterator) {
                            contentIterator += 1;
                        }
                        break;
                    case KeyCode.ArrowLeft:
                        if (dataHasHeader) {
                            if (contentIterator > 0) {
                                contentIterator -= 1;
                            } else if (headerIterator > 0) {
                                headerIterator -= 1;
                                contentIterator =
                                    (filteredData[headerIterator] as DropdownDataWithHeader).content.length - 1;
                            }
                        } else if (contentIterator > 0) {
                            contentIterator -= 1;
                        }
                        break;
                    case KeyCode.Enter:
                    case KeyCode.Tab:
                        handleItemSelect();
                        break;
                    case KeyCode.Esc:
                        handleOutsideClick();
                        return;
                    default:
                        return;
                }
                setHeaderIndex(headerIterator);
                setContentIndex(contentIterator);
                if (mergedDisplayOptions!.displayCategories) {
                    let categoryId = headerIterator;

                    if (filteredData[headerIterator] && filteredData[headerIterator].id) {
                        categoryId = findIndex(dropdownData, { id: filteredData[headerIterator].id });
                    }
                    setCategoryIndex(categoryId);
                }
                let node = dataContainerRef.current;

                if (node) {
                    // as recenlty, we have added a wrapper div as part of InfiniteScroll, so we need to access firstChild to reach the actual list container
                    node = node.firstChild as HTMLDivElement;
                }

                if (node !== null) {
                    if (dataHasHeader) {
                        (node.children[headerIterator].children[contentIterator + 1] as HTMLElement).scrollIntoView({
                            block: 'center',
                            behavior: 'auto',
                        });
                    } else {
                        (node.children[contentIterator] as HTMLElement).scrollIntoView({
                            block: 'center',
                            behavior: 'auto',
                        });
                    }
                }
            }
        };

        const handleItemMouseOver = (headerId: number, contentId: number): void => {
            setHeaderIndex(headerId);
            setContentIndex(contentId);
            if (mergedDisplayOptions!.displayCategories) {
                let categoryId = headerId;

                if (filteredData[headerId] && filteredData[headerId].id) {
                    categoryId = findIndex(dropdownData, { id: filteredData[headerId].id });
                }
                setCategoryIndex(categoryId);
            }
        };

        const handleItemSelect = (): void => {
            if (hasHeader()) {
                handlers.onSelect(get(filteredData, `[${headerIndex}].content[${contentIndex}]`, null));
            } else {
                handlers.onSelect(get(filteredData, `[${contentIndex}]`, null));
            }
        };

        const handleOutsideClick = (): void => {
            if (handlers && handlers.onOutsideClick) {
                handlers.onOutsideClick();
            }
        };

        const handleAutoSelect = (): void => {
            if (filteredData && filteredData.length) {
                if (hasHeader()) {
                    const randomHeaderIndex = Math.floor(Math.random() * filteredData.length) + 1;
                    const randomContentIndex =
                        Math.floor(
                            Math.random() *
                                (filteredData[randomHeaderIndex - 1] as DropdownDataWithHeader).content.length,
                        ) + 1;

                    handlers.onSelect(
                        (filteredData[randomHeaderIndex - 1] as DropdownDataWithHeader).content[randomContentIndex - 1],
                    );
                } else {
                    const randomContentIndex = Math.floor(Math.random() * filteredData.length) + 1;

                    handlers.onSelect((filteredData as any)[randomContentIndex - 1]);
                }
            }
        };

        const hasHeader = (): boolean => {
            return has(allData, '[0].header');
        };

        const getCategoriesData = (): any => {
            const categories: any = [];

            forEach(allData, (dataItem, dataKey) => {
                if ((dataItem as DropdownDataWithHeader).header) {
                    categories.push({
                        id: dataItem.id || dataKey,
                        label: (dataItem as DropdownDataWithHeader).header,
                        icon: (dataItem as DropdownDataWithHeader).icon,
                        customIconElement: (dataItem as DropdownDataWithHeader).customIconElement,
                        disabled: !some(filteredData, { header: (dataItem as DropdownDataWithHeader).header }),
                    });
                }
            });
            return categories;
        };

        const handleCategorySelection = (index: number, disabled: boolean): void => {
            if (!disabled) {
                let validIndex = index;

                if (allData[index] && allData[index].id) {
                    validIndex = findIndex(filteredData, { id: allData[index].id });
                }
                setHeaderIndex(validIndex);
                setCategoryIndex(index);
                setContentIndex(0);
                const node = dataContainerRef.current;

                if (node !== null) {
                    const nodeFirstChild = node.firstChild as HTMLElement;
                    const validIndexNode = nodeFirstChild && (nodeFirstChild.children[validIndex] as HTMLElement);
                    const hasOffsetTop = validIndexNode && validIndexNode.offsetTop;

                    if (hasOffsetTop) {
                        node.scrollTo(0, validIndexNode.offsetTop);
                    }
                }
            }
        };

        const getFooterData = (): DropdownData | null => {
            if (!filteredData || !filteredData.length) {
                return null;
            }
            if (hasHeader()) {
                return get(filteredData as DropdownDataWithHeader[], `[${headerIndex}].content[${contentIndex}]`, null);
            }
            return get(filteredData as DropdownData[], `[${contentIndex}]`, null);
        };

        return (
            <section ref={wrapperRef} className="r4e-dropdown-picker" style={{ width: mergedDisplayOptions!.width }}>
                <OutsideClick outsideClickHandler={handleOutsideClick}>
                    {mergedDisplayOptions!.displayCategories! ? (
                        <Categories
                            data={getCategoriesData()}
                            categoryIndex={categoryIndex}
                            handleCategorySelection={handleCategorySelection}
                        />
                    ) : null}
                    <SearchContainer
                        inputRef={inputRef}
                        searchOptions={mergedSearchOptions!}
                        searchText={searchText!}
                        enableAutoSelect={enableAutoSelect!}
                        handleSearchTextChange={handleSearchTextChange}
                        handleKeyNavigation={handleKeyNavigation}
                        handleAutoSelect={handleAutoSelect}
                    />
                    <DropdownDataContainer
                        wrapperRef={wrapperRef}
                        containerRef={dataContainerRef}
                        searchOptions={mergedSearchOptions!}
                        searchText={searchText!}
                        filteredData={filteredData}
                        hasDataLoaded={hasDataLoaded!}
                        displayOptions={mergedDisplayOptions!}
                        headerIndex={headerIndex}
                        contentIndex={contentIndex}
                        handleItemMouseOver={handleItemMouseOver}
                        handleItemSelect={handleItemSelect}
                        hasOverlayPromise={hasOverlayPromise}
                        overlayArea={overlayArea!}
                        onLoadMore={handleLoadMore}
                        hasMore={hasMoreData}
                        isLoadingMore={isLoadingMore}
                        handlers={handlers}
                    />
                    {mergedDisplayOptions!.displayFooter ? (
                        <Footer displayOptions={mergedDisplayOptions!} data={getFooterData()} searchText={searchText} />
                    ) : null}
                </OutsideClick>
            </section>
        );
    },
);

DropdownPicker.defaultProps = {
    searchOptions: {
        enable: true,
        defaultSearchText: '',
        highlightSearchText: true,
        closeWhenNoMatch: false,
    },
    displayOptions: {
        width: '400px',
        maxHeight: '400px',
        reviseMaxHeight: false,
        displayDescription: true,
        displayCategories: false,
        displayFooter: false,
        countPerRow: 1,
    },
    enableKeyNavigation: true,
    enableAutoSelect: false,
    autoFocus: false,
    hasDataLoaded: true,
    overlayArea: DropDownPickerOverlayArea,
    enableInfiniteScroll: false,
    itemsPerPage: 50,
};
